import { useState, useEffect, useCallback, useMemo } from "react";
import tw from "tailwind-styled-components";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import {
    BoxTitle,
    BoxContainer,
    BoxHead,
    Underline,
    FilterTableWrapper,
    FilterWrapperWithSearch,
} from "../../Components/Styles/PageStyles";
import { useSelector } from "react-redux";
import NotFoundModel from "../../Components/NotFoundModel";
import SideModal from "../../Components/SideModal";
import ViewTransferDetails from "../../Components/Payout/ViewTransferDetails";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import SearchFilterBox from "../../Components/SearchFilterBox";
import DateRange from "../../Components/DateRange";
import Table from "../../Components/Payout/PayoutHistoryTable";
import { IoChevronBackOutline } from "react-icons/io5";

const PayoutHistory = () => {
    const searchOptions = [
        { label: "Account", value: 1 },
        { label: "UTR", value: 2 },
    ];
    const navigate = useNavigate();
    const user = useSelector((state) => state.UserReducer.user);
    const location = useLocation();
    const [selectedData, setSelectedData] = useState("");
    const [apiData, setApiData] = useState([]);
    const [viewDetails, setViewDetails] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const { routeStartDate, routeEndDate } = location.state || {};
    const [startDate, setStartDate] = useState(
        routeStartDate || moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")
    );
    const [endDate, setEndDate] = useState(
        routeEndDate || moment(new Date()).format("YYYY-MM-DD")
    );
    const { id } = useParams();

    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        params.append('beneficiaryId', id);
        if (searchQuery) {
            params.append('s', searchQuery);
        }
        if (filterType) params.append('t', filterType);
        return params.toString();
    }, [startDate, endDate, searchQuery, filterType, id]);

    const fetchFunction = async () => {
        const query = buildQueryParams();

        const response = await axios.get(
            `${Config.apiUrl}/cashfree/payout/list/transfers?${query}`, {
            headers: {
                Authorization: `Bearer ${user.token}`,
            },
        });

        return response.data;
    };

    const getSuccess = (res) => {
        if (res.data === false) {
            setApiData([]);
        } else {
            const data = res?.data || [];
            setApiData(data);
            setFilteredData(data);
        }
    };

    const totalTransferred = useMemo(() => {
        return filteredData.reduce((sum, item) => {
            return item.status === 3
                ? sum + Number(item.amount || 0)
                : sum;
        }, 0);
    }, [filteredData]);

    const getError = () => {
        setApiData([]);
    };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        data,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            getListMutate();
        }, 100);
        return () => clearTimeout(handler);
    }, []);

    const submitHandler = (values) => {
        const newValues = {
            startDate: moment(values.startDate).format("YYYY-MM-DD"),
            endDate: moment(values.endDate).format("YYYY-MM-DD"),
        };
        setFilterType("");
        setSearchQuery("");
        setStartDate(newValues.startDate);
        setEndDate(newValues.endDate);
        const handler = setTimeout(() => {
            getListMutate();
        }, 100);
        return () => {
            clearTimeout(handler);
        };
    };

    const handleFilterChange = (values) => {
        setFilterType(values.filterType);
        setSearchQuery(values.searchQuery);
        const handler = setTimeout(() => {
            getListMutate();
        }, 100);
        return () => {
            clearTimeout(handler);
        };
    };

    const handleClearFilters = () => {
        setFilterType("");
        setSearchQuery("");
        const handler = setTimeout(() => {
            getListMutate();
        }, 100);
        return () => {
            clearTimeout(handler);
        };
    };

    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    <div className="flex cursor-pointer" onClick={() => navigate(-1)}>
                        <span className="items-center pt-[5px] mr-2">
                            <IoChevronBackOutline
                                size={20}
                            />
                        </span>
                        <div>
                            <span className="flex items-center cursor-pointer">
                                Transfers
                            </span>
                            <Underline />
                        </div>
                    </div>
                </BoxTitle>
            </BoxHead>

            <FilterTableWrapper>
                <FilterWrapperWithSearch>
                    <div className="flex items-center">
                        <Text>
                            Total Amount:{" "}
                            <span className="font-semibold">
                                {totalTransferred || 0}
                            </span>
                        </Text>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <SearchFilterBox
                            options={searchOptions}
                            handleFilterChange={handleFilterChange}
                            handleClearFilters={handleClearFilters}
                        />
                        <DateRange
                            submitHandler={submitHandler}
                            defaultLabel={
                                moment(startDate).format("DD MMM yyyy") +
                                " - " +
                                moment(endDate).format("DD MMM yyyy")
                            }
                        />
                    </div>

                </FilterWrapperWithSearch>

                {!error && !isLoading && data && (
                    <TableWrapper>
                        <Table
                            apiData={error ? [] : filteredData}
                            setSelectedData={setSelectedData}
                            selectedData={selectedData}
                            setViewDetails={setViewDetails}
                        />
                        {(!error && apiData?.length === 0 && !isLoading) && (
                            <NotFoundModel />
                        )}
                    </TableWrapper>
                )}
            </FilterTableWrapper>
            {(isLoading || isRefetching) && <Loading />}
            {error && !isLoading && <NotFoundModel />}


            <SideModal
                isOpen={viewDetails}
                onClose={() => setViewDetails(false)}
            >
                <ViewTransferDetails
                    setViewDetails={setViewDetails}
                    selectedData={selectedData}
                />
            </SideModal>
        </BoxContainer>
    );
};

const TableWrapper = tw.div`mt-4 bg-white rounded-md `;
const Text = tw.p`text-gray-500 text-sm font-normal`;

export default PayoutHistory;